import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { Loader } from "@/components/general/common/loader";

interface DataTableProps<TData> {
    columns: ColumnDef<TData>[];
    data: TData[];
    isLoading?: boolean;
}

interface RowData {
    stat: string;
    playerA: string | number;
    playerB: string | number;
    isTotal?: boolean;
}

export function ComparisonEngineStatsTable<TData extends object>({
    columns,
    data,
    isLoading = false,
}: DataTableProps<TData>) {
    const displayCols = columns;

    const table = useReactTable({
        data,
        columns: displayCols,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div
            className="w-full flex-1 min-h-0 data-table-body overflow-x-auto"
            style={{
                border: "1px solid rgba(199, 197, 214, 0.2)",
            }}
        >
            <div className="h-full">
                {data.length === 0 && !isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <span className="text-center p-4 text-on-surface-variant">
                            No data available for comparison.
                        </span>
                    </div>
                ) : (
                    <Table className="w-full">
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (
                                <TableRow key={hg.id} style={{ height: "40px" }}>
                                    {hg.headers.map((header, idx) => {
                                        const colId = header.column.id;
                                        const isStatCol = colId === "stat";
                                        const isPlayerACol = colId === "playerA";
                                        const isPlayerBCol = colId === "playerB";

                                        const thClasses = `
                                        py-2 px-3 font-headline font-bold h-10 text-[13px]
                                        sticky z-20 select-none transition-colors
                                        ${isStatCol ? "text-left text-on-surface" : ""}
                                        ${isPlayerACol ? "text-center text-blue-accent" : ""}
                                        ${isPlayerBCol ? "text-center text-orange-accent" : ""}
                                        ${!isStatCol && !isPlayerACol && !isPlayerBCol ? "text-center text-on-surface" : ""}
                                        ${idx === 0 ? "left-0 z-30" : ""}
                                    `;

                                        return (
                                            <TableHead
                                                key={header.id}
                                                className={`${thClasses} data-table-header`}
                                                style={{
                                                    position: "sticky",
                                                    top: 0,
                                                    height: "40px",
                                                    width: "33.33%",
                                                    color: isPlayerACol
                                                        ? "var(--color-chart-vivid-blue)"
                                                        : isPlayerBCol
                                                          ? "#ff9500"
                                                          : undefined,
                                                }}
                                            >
                                                {!header.isPlaceholder &&
                                                    flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <>
                                    {[
                                        "Defensive Points",
                                        "Offensive Points",
                                        "Team Play Points",
                                        "Effort Points",
                                        "Bonus Points",
                                        "Total Points",
                                    ].map((statName, rowIdx) => {
                                        const isTotal = statName === "Total Points";
                                        return (
                                            <TableRow
                                                key={statName}
                                                className="transition-colors"
                                                style={{
                                                    borderBottom: "1px solid rgba(199, 197, 214, 0.15)",
                                                    background: isTotal
                                                        ? "rgba(5, 0, 102, 0.04)"
                                                        : rowIdx % 2 === 1
                                                          ? "#f6f3f2"
                                                          : "transparent",
                                                    height: "40px",
                                                    borderTop: isTotal
                                                        ? "1px solid rgba(199, 197, 214, 0.4)"
                                                        : undefined,
                                                }}
                                            >
                                                <TableCell
                                                    className="py-2 px-3 h-10 text-[14px] sticky left-0 z-10 text-left"
                                                    style={{
                                                        fontWeight: isTotal ? 700 : 600,
                                                        color: "#050066",
                                                        background: isTotal ? "rgba(5, 0, 102, 0.04)" : "#ebe7e7",
                                                        borderRight: "1px solid rgba(199, 197, 214, 0.3)",
                                                        height: "40px",
                                                        width: "33.33%",
                                                    }}
                                                >
                                                    <span
                                                        className={
                                                            isTotal
                                                                ? "font-bold text-primary"
                                                                : "font-semibold text-on-surface"
                                                        }
                                                    >
                                                        {statName}
                                                    </span>
                                                </TableCell>
                                                {rowIdx === 0 ? (
                                                    <TableCell
                                                        colSpan={2}
                                                        className="h-64"
                                                        style={{
                                                            textAlign: "center",
                                                            background: "transparent",
                                                        }}
                                                    >
                                                        <div className="flex items-center justify-center h-full">
                                                            <Loader message="Loading player statistics..." />
                                                        </div>
                                                    </TableCell>
                                                ) : (
                                                    <>
                                                        <TableCell
                                                            className="py-2 px-3 h-10 text-[14px] text-center"
                                                            style={{
                                                                height: "40px",
                                                                width: "33.33%",
                                                                background: "transparent",
                                                            }}
                                                        />
                                                        <TableCell
                                                            className="py-2 px-3 h-10 text-[14px] text-center"
                                                            style={{
                                                                height: "40px",
                                                                width: "33.33%",
                                                                background: "transparent",
                                                            }}
                                                        />
                                                    </>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </>
                            ) : (
                                table
                                    .getRowModel()
                                    .rows.slice(0, 8)
                                    .map((row, rowIdx) => {
                                        const rowData = row.original as RowData;
                                        const isTotal = rowData.isTotal;

                                        return (
                                            <TableRow
                                                key={row.id}
                                                className="transition-colors"
                                                style={{
                                                    borderBottom: "1px solid rgba(199, 197, 214, 0.15)",
                                                    background: isTotal
                                                        ? "rgba(5, 0, 102, 0.04)"
                                                        : rowIdx % 2 === 1
                                                          ? "#f6f3f2"
                                                          : "transparent",
                                                    height: "40px",
                                                    borderTop: isTotal
                                                        ? "1px solid rgba(199, 197, 214, 0.4)"
                                                        : undefined,
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isTotal) {
                                                        e.currentTarget.style.background = "rgba(5, 0, 102, 0.04)";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isTotal) {
                                                        e.currentTarget.style.background =
                                                            rowIdx % 2 === 1 ? "#f6f3f2" : "transparent";
                                                    }
                                                }}
                                            >
                                                {row.getVisibleCells().map((cell, idx) => {
                                                    const colId = cell.column.id;
                                                    const isStatCol = colId === "stat";
                                                    const isPlayerACol = colId === "playerA";
                                                    const isPlayerBCol = colId === "playerB";

                                                    const tdClasses = `
                                                py-2 px-3 h-10 text-[14px] text-on-surface
                                                ${idx === 0 ? "sticky left-0 z-10" : ""}
                                                ${isStatCol ? "text-left" : "text-center"}
                                            `;

                                                    return (
                                                        <TableCell
                                                            key={cell.id}
                                                            className={tdClasses}
                                                            style={{
                                                                fontWeight: isTotal ? 700 : idx === 0 ? 600 : 400,
                                                                color: isPlayerACol
                                                                    ? "var(--color-primary)"
                                                                    : isPlayerBCol
                                                                      ? "var(--color-green)"
                                                                      : idx === 0
                                                                        ? "#050066"
                                                                        : undefined,
                                                                background:
                                                                    idx === 0
                                                                        ? isTotal
                                                                            ? "rgba(5, 0, 102, 0.04)"
                                                                            : "#ebe7e7"
                                                                        : undefined,
                                                                borderRight:
                                                                    idx === 0
                                                                        ? "1px solid rgba(199, 197, 214, 0.3)"
                                                                        : undefined,
                                                                height: "40px",
                                                                width: "33.33%",
                                                            }}
                                                        >
                                                            <span
                                                                className={
                                                                    isPlayerACol
                                                                        ? "text-blue-accent font-semibold"
                                                                        : isPlayerBCol
                                                                          ? "text-orange-accent font-semibold"
                                                                          : isTotal
                                                                            ? "font-bold text-primary"
                                                                            : "font-semibold text-on-surface-variant"
                                                                }
                                                            >
                                                                {flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )}
                                                            </span>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    })
                            )}
                        </TableBody>
                    </Table>
                )}
            </div>
        </div>
    );
}

export default ComparisonEngineStatsTable;
